import React, { useEffect, useMemo, useState } from 'react';
import { Card, Badge, DataTable, Button, Input, Modal } from '../../../components/ui';
import { RefreshCw, Building2, Users, Plus, KeyRound, Power } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';

interface LimiteInfo {
  max_vendedores: number;
  usados: number;
  disponibles: number;
}

interface LimiteLocalesInfo {
  max_locales: number;
  usados: number;
  disponibles: number;
}

interface CrearLocalForm {
  nombre: string;
  direccion: string;
  telefono: string;
}

interface LocalResumenItem {
  local_id: string;
  nombre_local: string;
  local_username?: string | null;
  local_activo: boolean;
  vendedores_activos: number;
  vendedores_total: number;
}

interface LocalSimpleItem {
  id: string;
  nombre: string;
  activo: boolean;
}

export const BancaVendedoresPage: React.FC = () => {
  const [localesResumen, setLocalesResumen] = useState<LocalResumenItem[]>([]);
  const [limite, setLimite] = useState<LimiteInfo>({ max_vendedores: 0, usados: 0, disponibles: 0 });
  const [limiteLocales, setLimiteLocales] = useState<LimiteLocalesInfo>({ max_locales: 0, usados: 0, disponibles: 0 });
  const [loading, setLoading] = useState(false);
  const [savingLocal, setSavingLocal] = useState(false);
  const [savingAction, setSavingAction] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [credModalOpen, setCredModalOpen] = useState(false);
  const [selectedLocal, setSelectedLocal] = useState<LocalResumenItem | null>(null);
  const [credForm, setCredForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [form, setForm] = useState<CrearLocalForm>({ nombre: '', direccion: '', telefono: '' });

  const cargarDatos = async () => {
    setLoading(true);
    setError('');
    try {
      const [resumenData, limiteData, limiteLocalesData] = await Promise.all([
        fetchAPI<LocalResumenItem[]>('/usuarios/banca/locales/resumen-vendedores'),
        fetchAPI<LimiteInfo>('/usuarios/banca/vendedores/limite'),
        fetchAPI<LimiteLocalesInfo>('/usuarios/banca/locales/limite'),
      ]);
      setLocalesResumen(resumenData);
      setLimite(limiteData);
      setLimiteLocales(limiteLocalesData);
    } catch (e: any) {
      setError(e.message || 'No se pudo cargar el resumen de vendedores por local');
    } finally {
      setLoading(false);
    }
  };

  const crearLocal = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.nombre.trim()) {
      setError('Nombre del local es obligatorio');
      return;
    }

    setSavingLocal(true);
    try {
      await fetchAPI<LocalSimpleItem>('/usuarios/banca/locales', {
        method: 'POST',
        body: JSON.stringify({
          nombre: form.nombre.trim(),
          direccion: form.direccion.trim() || null,
          telefono: form.telefono.trim() || null,
        }),
      });
      setModalOpen(false);
      setForm({ nombre: '', direccion: '', telefono: '' });
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || 'No se pudo crear el local');
    } finally {
      setSavingLocal(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const totalLocales = useMemo(() => localesResumen.length, [localesResumen]);

  const abrirCredenciales = (local: LocalResumenItem) => {
    setSelectedLocal(local);
    setCredForm({ username: local.local_username || '', password: '' });
    setCredModalOpen(true);
    setError('');
  };

  const guardarCredenciales = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocal) return;

    const username = credForm.username.trim();
    const password = credForm.password.trim();

    if (!username && !password) {
      setError('Debes indicar un usuario y/o contraseña');
      return;
    }

    setSavingAction(true);
    setError('');
    try {
      await fetchAPI<LocalResumenItem>(`/usuarios/banca/locales/${selectedLocal.local_id}/credenciales`, {
        method: 'PATCH',
        body: JSON.stringify({
          username: username || undefined,
          password: password || undefined,
        }),
      });
      setCredModalOpen(false);
      setSelectedLocal(null);
      setCredForm({ username: '', password: '' });
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || 'No se pudieron actualizar las credenciales del local');
    } finally {
      setSavingAction(false);
    }
  };

  const cambiarEstadoLocal = async (local: LocalResumenItem) => {
    const accion = local.local_activo ? 'inactivar' : 'activar';
    const mensaje = local.local_activo
      ? `¿Seguro que deseas inactivar ${local.nombre_local}? Se inactivarán también sus vendedores activos.`
      : `¿Seguro que deseas activar ${local.nombre_local}?`;

    if (!window.confirm(mensaje)) return;

    setSavingAction(true);
    setError('');
    try {
      await fetchAPI<LocalResumenItem>(`/usuarios/banca/locales/${local.local_id}/estado`, {
        method: 'PATCH',
        body: JSON.stringify({ activo: !local.local_activo }),
      });
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || `No se pudo ${accion} el local`);
    } finally {
      setSavingAction(false);
    }
  };

  const columns = [
    {
      key: 'local',
      header: 'Local',
      render: (l: LocalResumenItem) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
            <Building2 className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{l.nombre_local}</p>
            <p className="text-sm text-gray-500">{l.local_id}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'estado',
      header: 'Estado local',
      render: (l: LocalResumenItem) => (
        <Badge variant={l.local_activo ? 'success' : 'neutral'}>
          {l.local_activo ? 'ACTIVO' : 'INACTIVO'}
        </Badge>
      ),
    },
    {
      key: 'usuario_local',
      header: 'Usuario local',
      render: (l: LocalResumenItem) => <span className="font-medium">{l.local_username || '-'}</span>,
    },
    {
      key: 'vendedores_activos',
      header: 'Vendedores activos',
      render: (l: LocalResumenItem) => (
        <span className="font-semibold text-primary-700 flex items-center gap-1">
          <Users size={14} />
          {l.vendedores_activos}
        </span>
      ),
    },
    {
      key: 'vendedores_total',
      header: 'Vendedores totales',
      render: (l: LocalResumenItem) => <span className="font-medium">{l.vendedores_total}</span>,
    },
    {
      key: 'acciones',
      header: 'Acciones',
      render: (l: LocalResumenItem) => (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<KeyRound size={14} />}
            onClick={() => abrirCredenciales(l)}
            disabled={savingAction}
          >
            Credenciales
          </Button>
          <Button
            size="sm"
            variant={l.local_activo ? 'warning' : 'success'}
            leftIcon={<Power size={14} />}
            onClick={() => cambiarEstadoLocal(l)}
            disabled={savingAction}
          >
            {l.local_activo ? 'Inactivar' : 'Activar'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resumen de Vendedores</h1>
          <p className="text-gray-500">BANCA solo visualiza locales y cantidad de vendedores por local</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={cargarDatos}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
            title="Refrescar"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <Button
            variant="primary"
            leftIcon={<Plus size={18} />}
            onClick={() => setModalOpen(true)}
            disabled={limiteLocales.disponibles <= 0}
          >
            Nuevo Local
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <p className="text-sm text-gray-500">Límite ADMIN</p>
          <p className="text-2xl font-bold">{limite.max_vendedores}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Vendedores activos (banca)</p>
          <p className="text-2xl font-bold text-primary-600">{limite.usados}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Locales</p>
          <p className="text-2xl font-bold text-success-600">{totalLocales}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Locales disponibles</p>
          <p className={`text-2xl font-bold ${limiteLocales.disponibles > 0 ? 'text-success-600' : 'text-danger-600'}`}>
            {limiteLocales.disponibles}
          </p>
        </Card>
      </div>

      {error && <p className="text-sm text-danger-600">{error}</p>}

      <Card padding="none">
        <DataTable
          columns={columns}
          data={localesResumen}
          keyExtractor={(l: LocalResumenItem) => l.local_id}
          isLoading={loading}
          emptyMessage="No hay locales registrados en esta banca"
        />
      </Card>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Crear nuevo local"
        size="md"
      >
        <form onSubmit={crearLocal} className="space-y-4">
          <Input
            label="Nombre"
            value={form.nombre}
            onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
            required
          />
          <Input
            label="Dirección (opcional)"
            value={form.direccion}
            onChange={(e) => setForm((p) => ({ ...p, direccion: e.target.value }))}
          />
          <Input
            label="Teléfono (opcional)"
            value={form.telefono}
            onChange={(e) => setForm((p) => ({ ...p, telefono: e.target.value }))}
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={savingLocal}>Crear local</Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={credModalOpen}
        onClose={() => {
          setCredModalOpen(false);
          setSelectedLocal(null);
          setCredForm({ username: '', password: '' });
        }}
        title={`Credenciales local${selectedLocal ? `: ${selectedLocal.nombre_local}` : ''}`}
        size="md"
      >
        <form onSubmit={guardarCredenciales} className="space-y-4">
          <Input
            label="Usuario"
            value={credForm.username}
            onChange={(e) => setCredForm((p) => ({ ...p, username: e.target.value }))}
            placeholder="Usuario del local"
          />
          <Input
            label="Nueva contraseña (opcional)"
            type="password"
            value={credForm.password}
            onChange={(e) => setCredForm((p) => ({ ...p, password: e.target.value }))}
            placeholder="Mínimo 6 caracteres"
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setCredModalOpen(false);
                setSelectedLocal(null);
                setCredForm({ username: '', password: '' });
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={savingAction}>Guardar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default BancaVendedoresPage;
